"use client";

import Link from "@/infrastructure/router/Link";
import { type CSSProperties, useEffect, useMemo, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	ArtworkThumbnail,
	EmptyState,
	GlassButton,
	GlassCard,
	GlassIconButton,
	GlassTextField,
	MediaRow,
	MoreDotsButton,
	NavigationBar,
	PlaylistPlaceholder,
	SheetHeader,
	Skeleton,
} from "@/components/design-system/ArtistDesignSystem";
import {
	browserDiscoveryRepository,
	type DiscoveryPlaylist,
	type DiscoveryRepository,
	type DiscoveryTrack,
	playbackHref,
} from "@/domain/discovery/DiscoveryRepository";
import { formatDuration } from "./DiscoveryPrimitives";

type SortOption = "default" | "title" | "artist" | "album" | "duration";

const SORT_OPTIONS: readonly { value: SortOption; label: string }[] = [
	{ value: "default", label: "原序" },
	{ value: "title", label: "按标题" },
	{ value: "artist", label: "按艺术家" },
	{ value: "album", label: "按专辑" },
	{ value: "duration", label: "按时长" },
];

const PLAYLIST_SKELETON_ROWS: readonly string[] = Array.from(
	{ length: 6 },
	(_, index) => `playlist-skeleton-row-${index}`,
);

function sortTracks(
	tracks: readonly DiscoveryTrack[],
	option: SortOption,
): DiscoveryTrack[] {
	const base = [...tracks];
	switch (option) {
		case "title":
			return base.sort((a, b) =>
				a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
			);
		case "artist":
			return base.sort((a, b) =>
				a.artist.localeCompare(b.artist, undefined, { sensitivity: "base" }),
			);
		case "album":
			return base.sort((a, b) =>
				a.genre.localeCompare(b.genre, undefined, { sensitivity: "base" }),
			);
		case "duration":
			return base.sort((a, b) => a.durationSeconds - b.durationSeconds);
		default:
			return base;
	}
}

function ShuffleGlyph({ size = 16 }: Readonly<{ size?: number }>) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M3 5h4l10 14h4" />
			<path d="M3 19h4l3-4.2" />
			<path d="M14 9.2 17 5h4" />
			<path d="m17 5-3 3" />
			<path d="m17 19 3-3-3-3" />
			<path d="m3 5 3 3" />
		</svg>
	);
}

const TONE_GRADIENT: Record<DiscoveryTrack["artwork"], string> = {
	violet: "linear-gradient(135deg, hsl(268 48% 52%), hsl(296 52% 42%))",
	amber: "linear-gradient(135deg, hsl(32 60% 52%), hsl(12 64% 46%))",
	blue: "linear-gradient(135deg, hsl(212 60% 52%), hsl(232 56% 44%))",
	lime: "linear-gradient(135deg, hsl(142 48% 48%), hsl(102 52% 40%))",
	rose: "linear-gradient(135deg, hsl(342 56% 56%), hsl(322 60% 48%))",
};

function TrackArtwork({ tone }: Readonly<{ tone: DiscoveryTrack["artwork"] }>) {
	const style: CSSProperties = {
		width: "100%",
		height: "100%",
		borderRadius: "inherit",
		display: "inline-grid",
		placeItems: "center",
		color: "rgb(255 255 255 / 0.92)",
		background: TONE_GRADIENT[tone],
	};
	return (
		<span style={style} aria-hidden="true">
			<ArtistIcon name="music" size={20} />
		</span>
	);
}

const sheetOverlayStyle: CSSProperties = {
	position: "fixed",
	inset: 0,
	zIndex: 80,
	display: "grid",
	alignItems: "end",
	justifyItems: "center",
	padding: 0,
	background: "rgb(0 0 0 / 0.6)",
	backdropFilter: "blur(8px)",
	WebkitBackdropFilter: "blur(8px)",
};

const sheetStyle: CSSProperties = {
	width: "100%",
	maxWidth: 560,
	maxHeight: "85dvh",
	overflow: "auto",
	borderTopLeftRadius: 28,
	borderTopRightRadius: 28,
	borderBottomLeftRadius: 0,
	borderBottomRightRadius: 0,
	background: "var(--ds-surface)",
	boxShadow: "0 -20px 60px rgb(0 0 0 / 0.35)",
};

const sheetBodyStyle: CSSProperties = {
	display: "grid",
	gap: 16,
	padding: "16px 20px 32px",
};

const sheetGrabberStyle: CSSProperties = {
	width: 42,
	height: 5,
	margin: "10px auto 0",
	borderRadius: "var(--ds-radius-full)",
	background: "var(--ds-icon-secondary)",
	display: "block",
};

/**
 * PlaylistDetail — Web adaptation of iOS `PlaylistDetailView`.
 *
 * Maps the iOS structure to native HTML + DS primitives:
 * - `ScrollView` → scrollable `<article>` body
 * - `VStack(spacing: .lg)` → vertical stack with `gap`
 * - `headerSection` → `<section className="playlist-hero">` (cover + title + creator + meta)
 * - `actionButtonsSection` → `<section className="playlist-actions">` (Play All + Shuffle)
 * - `DSGlassTextField` → `GlassTextField` (in-page search)
 * - `descriptionSection` → `<p className="playlist-description">`
 * - `trackListSection` → `GlassCard` wrapping `<ul role="list">` of `MediaRow`
 * - `.toolbar` → `NavigationBar` trailing slot (download + more)
 * - `.sheet` → portal-style overlay (More / Edit / Sort sheets)
 * - `PlaylistTrackListSkeleton` → `Skeleton` based loader
 * - `DSEmptyState` → `EmptyState`
 */
export function PlaylistDetail({
	id,
	repository = browserDiscoveryRepository,
}: Readonly<{ id: string; repository?: DiscoveryRepository }>) {
	const auth = useArtistSession();
	const [playlist, setPlaylist] = useState<
		DiscoveryPlaylist | null | undefined
	>(undefined);
	const [error, setError] = useState<string | null>(null);
	const [searchText, setSearchText] = useState("");
	const [sortOption, setSortOption] = useState<SortOption>("default");
	const [reload, setReload] = useState(0);
	const [moreSheetOpen, setMoreSheetOpen] = useState(false);
	const [editSheetOpen, setEditSheetOpen] = useState(false);
	const [sortSheetOpen, setSortSheetOpen] = useState(false);
	const [draftName, setDraftName] = useState("");
	const [saving, setSaving] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);

	useEffect(() => {
		void reload;
		let active = true;
		setPlaylist(undefined);
		setError(null);
		repository
			.loadPlaylist(id, auth.scope)
			.then((value) => {
				if (active) {
					setPlaylist(value);
					setDraftName(value?.name ?? "");
				}
			})
			.catch(() => {
				if (active) setError("歌单仓库暂时无法读取。");
			});
		return () => {
			active = false;
		};
	}, [auth.scope, id, reload, repository]);

	const canEdit =
		playlist !== null &&
		playlist !== undefined &&
		!playlist.isSystem &&
		auth.scope !== null &&
		playlist.ownerUserId === auth.scope.ownerUserId;

	const trimmedSearch = searchText.trim();
	const displayedTracks = useMemo(() => {
		if (playlist === null || playlist === undefined) return [];
		const query = trimmedSearch.toLowerCase();
		const base =
			query.length === 0
				? [...playlist.tracks]
				: playlist.tracks.filter(
						(track) =>
							track.title.toLowerCase().includes(query) ||
							track.artist.toLowerCase().includes(query) ||
							track.genre.toLowerCase().includes(query),
					);
		return sortTracks(base, sortOption);
	}, [playlist, trimmedSearch, sortOption]);

	const playableTracks = useMemo(
		() =>
			playlist?.tracks.filter((track) => track.playableRequestId !== null) ??
			[],
		[playlist],
	);

	const totalDuration = useMemo(
		() =>
			playlist?.tracks.reduce((sum, track) => sum + track.durationSeconds, 0) ??
			0,
		[playlist],
	);

	if (error !== null) {
		return (
			<article className="playlist-detail native-page">
				<NavigationBar title="歌单" backHref="/discover" />
				<EmptyState
					title="歌单加载失败"
					message={error}
					symbol={<ArtistIcon name="info" size={36} />}
					actionLabel="重试"
					actionSymbol={<ArtistIcon name="sparkle" size={16} />}
					onAction={() => setReload((value) => value + 1)}
				/>
			</article>
		);
	}

	if (playlist === undefined) {
		return (
			<article className="playlist-detail native-page">
				<NavigationBar title="歌单" backHref="/discover" />
				<PlaylistDetailSkeleton />
			</article>
		);
	}

	if (playlist === null) {
		return (
			<article className="playlist-detail native-page">
				<NavigationBar title="歌单" backHref="/discover" />
				<EmptyState
					title="没有这个歌单"
					message="它可能属于另一个账户 scope，或已被移除。"
					symbol={<ArtistIcon name="info" size={36} />}
					actionLabel="返回发现"
					actionSymbol={<ArtistIcon name="back" size={16} />}
					onAction={() => window.location.assign("/discover")}
				/>
			</article>
		);
	}

	function startPlayAll() {
		if (playableTracks.length === 0) {
			setNotice("这个歌单没有可验证的播放凭证，播放入口保持关闭。");
			return;
		}
		const first = playableTracks[0];
		if (first === undefined) return;
		const href = playbackHref(first, playableTracks);
		if (href !== null) window.location.assign(href);
	}

	function startShuffle() {
		if (playableTracks.length === 0) {
			setNotice("这个歌单没有可验证的播放凭证，播放入口保持关闭。");
			return;
		}
		const shuffled = [...playableTracks];
		for (let i = shuffled.length - 1; i > 0; i -= 1) {
			const j = Math.floor(Math.random() * (i + 1));
			const current = shuffled[i];
			const replacement = shuffled[j];
			if (current !== undefined && replacement !== undefined) {
				shuffled[i] = replacement;
				shuffled[j] = current;
			}
		}
		const first = shuffled[0];
		if (first === undefined) return;
		const href = playbackHref(first, shuffled);
		if (href !== null) window.location.assign(href);
	}

	async function saveName() {
		if (!canEdit || auth.scope === null || playlist === null) return;
		const trimmed = draftName.trim();
		if (trimmed === "") return;
		setSaving(true);
		setError(null);
		try {
			const saved = await repository.savePlaylist(
				{ ...playlist, name: trimmed },
				auth.scope,
			);
			setPlaylist(saved);
			setEditSheetOpen(false);
		} catch {
			setError("歌单名称未保存；原数据保持不变。");
		} finally {
			setSaving(false);
		}
	}

	const displayedName = playlist.name;
	const trackCount = playlist.tracks.length;
	const totalDurationLabel = formatDuration(totalDuration);
	const playableCount = playableTracks.length;

	return (
		<article className="playlist-detail native-page">
			<NavigationBar
				title={displayedName}
				backHref="/discover"
				trailing={
					<>
						<GlassIconButton
							label="下载"
							disabled={trackCount === 0}
							onClick={() =>
								setNotice("Web 暂不支持离线下载，请使用原生客户端。")
							}
						>
							<ArtistIcon name="download" size={20} />
						</GlassIconButton>
						<MoreDotsButton
							label="更多操作"
							onClick={() => setMoreSheetOpen(true)}
						/>
					</>
				}
			/>

			<div className="playlist-detail__body">
				{/* Header — cover + title + creator + meta */}
				<section className="playlist-hero" aria-label="歌单信息">
					<span className="playlist-hero__cover">
						<PlaylistPlaceholder seed={playlist.id} size={220} />
					</span>
					<h1 className="playlist-hero__title">{displayedName}</h1>
					<p className="playlist-hero__creator">{playlist.createdBy}</p>
					<p className="playlist-hero__meta">
						{trackCount} 首{trackCount > 0 ? ` · ${totalDurationLabel}` : ""}
					</p>
				</section>

				{/* Action Buttons — Play All + Shuffle */}
				<section className="playlist-actions" aria-label="播放操作">
					<GlassButton
						kind="primary"
						size="lg"
						expands
						symbol={<ArtistIcon name="play" size={16} />}
						isEnabled={playableCount > 0}
						onClick={startPlayAll}
					>
						全部播放
					</GlassButton>
					<GlassButton
						kind="secondary"
						size="lg"
						expands
						symbol={<ShuffleGlyph size={16} />}
						isEnabled={playableCount > 0}
						onClick={startShuffle}
					>
						随机播放
					</GlassButton>
				</section>

				{/* In-page Search */}
				<GlassTextField
					label="搜索歌单内曲目"
					leading={<ArtistIcon name="search" size={16} />}
					placeholder="按标题、艺术家或流派"
					value={searchText}
					onChange={(event) => setSearchText(event.target.value)}
				/>

				{/* Description */}
				{playlist.description.length > 0 ? (
					<p className="playlist-description">{playlist.description}</p>
				) : null}

				{/* Capability gate — catalog viewable, media not authorized */}
				{playableCount === 0 && trackCount > 0 ? (
					<div className="playlist-notice" role="status">
						<strong>目录可浏览，媒体未授权</strong>
						<p>
							这个公开系统歌单没有可验证的播放凭证，因此播放和随机入口保持关闭。
						</p>
						<button type="button" onClick={() => setNotice(null)}>
							知道了
						</button>
					</div>
				) : null}

				{/* Inline notice */}
				{notice !== null && !(playableCount === 0 && trackCount > 0) ? (
					<div className="playlist-notice" role="status">
						<strong>提示</strong>
						<p>{notice}</p>
						<button type="button" onClick={() => setNotice(null)}>
							知道了
						</button>
					</div>
				) : null}

				{/* Track List / Empty / No-match */}
				{displayedTracks.length === 0 ? (
					trimmedSearch.length === 0 ? (
						<EmptyState
							title="歌单还是空的"
							message={
								canEdit
									? "新的 ready 作品会自动加入；也可以返回创作。"
									: "这个歌单目前没有曲目。"
							}
							symbol={<ArtistIcon name="music" size={36} />}
							{...(canEdit
								? {
										actionLabel: "开始创作" as const,
										actionSymbol: <ArtistIcon name="sparkle" size={16} />,
										onAction: () => window.location.assign("/genart"),
									}
								: {})}
						/>
					) : (
						<EmptyState
							title="没有匹配的曲目"
							message={`没有标题、艺术家或流派包含"${trimmedSearch}"的曲目。`}
							symbol={<ArtistIcon name="search" size={36} />}
						/>
					)
				) : (
					<GlassCard
						className="playlist-track-list-card"
						style={{ padding: 0 }}
					>
						<ul className="playlist-track-list">
							{displayedTracks.map((track, index) => {
								const href = playbackHref(track, displayedTracks);
								const rowProps =
									href === null
										? {
												onClick: () =>
													setNotice("该目录条目没有已验证媒体，未进入播放器。"),
											}
										: { href };
								return (
									<li key={track.id} className="playlist-track-list__item">
										<MediaRow
											leading={
												<ArtworkThumbnail size={56} alt={`${track.title} 封面`}>
													<TrackArtwork tone={track.artwork} />
												</ArtworkThumbnail>
											}
											title={track.title}
											subtitle={`${track.artist} · ${track.genre}`}
											trailing={
												<span className="playlist-track-duration">
													{formatDuration(track.durationSeconds)}
												</span>
											}
											{...rowProps}
										/>
										{index < displayedTracks.length - 1 ? (
											<hr className="playlist-track-divider" />
										) : null}
									</li>
								);
							})}
						</ul>
					</GlassCard>
				)}

				{/* Footer — back + refresh + scope */}
				<footer className="playlist-detail__footer">
					<Link href="/discover" aria-label="返回发现">
						← 返回发现
					</Link>
					<button type="button" onClick={() => setReload((value) => value + 1)}>
						刷新歌单
					</button>
					<span>
						{playlist.isSystem
							? "系统歌单 · 只读"
							: canEdit
								? "当前 owner · 可编辑"
								: "非当前 owner · 只读"}
					</span>
				</footer>
			</div>

			{/* More Sheet */}
			{moreSheetOpen ? (
				<PlaylistMoreSheet
					canEdit={canEdit}
					canMove={canEdit && trackCount > 0}
					canDelete={canEdit}
					canReport={false}
					trackCount={trackCount}
					onClose={() => setMoreSheetOpen(false)}
					onMove={() => {
						setMoreSheetOpen(false);
						setNotice("Web 暂不支持重新排序，请使用原生客户端。");
					}}
					onAddToPlaylist={() => {
						setMoreSheetOpen(false);
						setNotice("Web 暂不支持添加到其他歌单。");
					}}
					onEdit={() => {
						setMoreSheetOpen(false);
						setEditSheetOpen(true);
					}}
					onSort={() => {
						setMoreSheetOpen(false);
						setSortSheetOpen(true);
					}}
					onShare={async () => {
						setMoreSheetOpen(false);
						if (
							typeof navigator !== "undefined" &&
							navigator.share !== undefined
						) {
							try {
								await navigator.share({
									title: playlist.name,
									url: window.location.href,
								});
							} catch {
								/* user dismissed */
							}
						} else {
							setNotice("当前浏览器不支持系统分享。");
						}
					}}
					onReport={() => {
						setMoreSheetOpen(false);
						setNotice("Web 暂不支持举报歌单。");
					}}
					onDelete={() => {
						setMoreSheetOpen(false);
						setNotice("Web 暂不支持删除歌单，请使用原生客户端。");
					}}
				/>
			) : null}

			{/* Edit Sheet */}
			{editSheetOpen ? (
				<PlaylistEditSheet
					name={draftName}
					onNameChange={setDraftName}
					saving={saving}
					canSave={canEdit && draftName.trim() !== "" && !saving}
					onClose={() => {
						setEditSheetOpen(false);
						setDraftName(playlist.name);
					}}
					onSave={() => void saveName()}
				/>
			) : null}

			{/* Sort Sheet */}
			{sortSheetOpen ? (
				<PlaylistSortSheet
					selected={sortOption}
					onClose={() => setSortSheetOpen(false)}
					onSelect={(option) => {
						setSortOption(option);
						setSortSheetOpen(false);
					}}
				/>
			) : null}
		</article>
	);
}

/**
 * PlaylistDetailSkeleton — loading skeleton matching the iOS
 * `PlaylistSkeletonView` (header + actions + track list).
 */
function PlaylistDetailSkeleton() {
	return (
		<div className="playlist-skeleton" role="status" aria-label="正在加载歌单">
			<div className="playlist-skeleton__hero">
				<Skeleton shape="rectangle" width={220} height={220} />
				<Skeleton shape="rectangle" width={180} height={28} />
				<Skeleton shape="rectangle" width={120} height={14} />
				<Skeleton shape="rectangle" width={100} height={12} />
			</div>
			<div className="playlist-skeleton__actions">
				<Skeleton shape="capsule" height={50} />
				<Skeleton shape="capsule" height={50} />
			</div>
			<ul className="playlist-skeleton__list">
			{PLAYLIST_SKELETON_ROWS.map((rowId) => (
				<li
					key={rowId}
					className="playlist-skeleton__row"
				>
					<Skeleton shape="rectangle" width={56} height={56} />
					<span className="playlist-skeleton__copy">
						<Skeleton shape="rectangle" width={180} height={16} />
						<Skeleton shape="rectangle" width={120} height={12} />
					</span>
					<Skeleton shape="rectangle" width={36} height={12} />
				</li>
			))}
		</ul>
		</div>
	);
}

interface MoreSheetProps {
	readonly canEdit: boolean;
	readonly canMove: boolean;
	readonly canDelete: boolean;
	readonly canReport: boolean;
	readonly trackCount: number;
	readonly onClose: () => void;
	readonly onMove: () => void;
	readonly onAddToPlaylist: () => void;
	readonly onEdit: () => void;
	readonly onSort: () => void;
	readonly onShare: () => void | Promise<void>;
	readonly onReport: () => void;
	readonly onDelete: () => void;
}

function PlaylistMoreSheet({
	canEdit,
	canMove,
	canDelete,
	canReport,
	trackCount,
	onClose,
	onMove,
	onAddToPlaylist,
	onEdit,
	onSort,
	onShare,
	onReport,
	onDelete,
}: Readonly<MoreSheetProps>) {
	return (
		<div
			className="playlist-sheet-overlay"
			style={sheetOverlayStyle}
			role="dialog"
			aria-modal="true"
			aria-label="歌单更多操作"
			onClick={onClose}
			onKeyDown={(event) => {
				if (event.key === "Escape") onClose();
			}}
		>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation prevents backdrop close when clicking inside the sheet; not a keyboard-interactive element */}
			{/* biome-ignore lint/a11y/noStaticElementInteractions: presentational sheet container; click handler only prevents event bubbling to the overlay */}
			<div
				className="playlist-sheet"
				style={sheetStyle}
				onClick={(event) => event.stopPropagation()}
			>
				<span aria-hidden="true" style={sheetGrabberStyle} />
				<SheetHeader title="更多操作" leading="cancel" onLeading={onClose} />
				<ul style={sheetBodyStyle} className="playlist-sheet__list">
					<li>
						<button
							type="button"
							className="playlist-sheet__item"
							onClick={onMove}
							disabled={!canMove}
						>
							<ArtistIcon name="sliders" size={18} />
							<span>重新排序</span>
						</button>
					</li>
					<li>
						<button
							type="button"
							className="playlist-sheet__item"
							onClick={onAddToPlaylist}
							disabled={trackCount === 0}
						>
							<ArtistIcon name="plus" size={18} />
							<span>添加到其他歌单</span>
						</button>
					</li>
					<li>
						<button
							type="button"
							className="playlist-sheet__item"
							onClick={onEdit}
							disabled={!canEdit}
						>
							<ArtistIcon name="create" size={18} />
							<span>编辑歌单</span>
						</button>
					</li>
					<li>
						<button
							type="button"
							className="playlist-sheet__item"
							onClick={onSort}
						>
							<ArtistIcon name="sliders" size={18} />
							<span>排序</span>
						</button>
					</li>
					<li>
						<button
							type="button"
							className="playlist-sheet__item"
							onClick={() => void onShare()}
						>
							<ArtistIcon name="share" size={18} />
							<span>分享</span>
						</button>
					</li>
					{canReport ? (
						<li>
							<button
								type="button"
								className="playlist-sheet__item playlist-sheet__item--destructive"
								onClick={onReport}
							>
								<ArtistIcon name="info" size={18} />
								<span>举报</span>
							</button>
						</li>
					) : null}
					{canDelete ? (
						<li>
							<button
								type="button"
								className="playlist-sheet__item playlist-sheet__item--destructive"
								onClick={onDelete}
							>
								<ArtistIcon name="trash" size={18} />
								<span>从资料库删除</span>
							</button>
						</li>
					) : null}
				</ul>
			</div>
		</div>
	);
}

interface EditSheetProps {
	readonly name: string;
	readonly onNameChange: (value: string) => void;
	readonly saving: boolean;
	readonly canSave: boolean;
	readonly onClose: () => void;
	readonly onSave: () => void;
}

function PlaylistEditSheet({
	name,
	onNameChange,
	saving,
	canSave,
	onClose,
	onSave,
}: Readonly<EditSheetProps>) {
	return (
		<div
			className="playlist-sheet-overlay"
			style={sheetOverlayStyle}
			role="dialog"
			aria-modal="true"
			aria-label="编辑歌单"
			onClick={onClose}
			onKeyDown={(event) => {
				if (event.key === "Escape") onClose();
			}}
		>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation prevents backdrop close when clicking inside the sheet; not a keyboard-interactive element */}
			{/* biome-ignore lint/a11y/noStaticElementInteractions: presentational sheet container; click handler only prevents event bubbling to the overlay */}
			<div
				className="playlist-sheet"
				style={sheetStyle}
				onClick={(event) => event.stopPropagation()}
			>
				<span aria-hidden="true" style={sheetGrabberStyle} />
				<SheetHeader title="编辑歌单" leading="cancel" onLeading={onClose} />
				<div style={sheetBodyStyle}>
					<GlassTextField
						label="名称"
						leading={<ArtistIcon name="music" size={16} />}
						placeholder="给歌单起个名字"
						value={name}
						onChange={(event) => onNameChange(event.target.value)}
						maxLength={80}
					/>
					<GlassButton
						kind="primary"
						size="md"
						expands
						isEnabled={canSave}
						isLoading={saving}
						onClick={onSave}
					>
						保存
					</GlassButton>
				</div>
			</div>
		</div>
	);
}

interface SortSheetProps {
	readonly selected: SortOption;
	readonly onClose: () => void;
	readonly onSelect: (option: SortOption) => void;
}

function PlaylistSortSheet({
	selected,
	onClose,
	onSelect,
}: Readonly<SortSheetProps>) {
	return (
		<div
			className="playlist-sheet-overlay"
			style={sheetOverlayStyle}
			role="dialog"
			aria-modal="true"
			aria-label="排序方式"
			onClick={onClose}
			onKeyDown={(event) => {
				if (event.key === "Escape") onClose();
			}}
		>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation prevents backdrop close when clicking inside the sheet; not a keyboard-interactive element */}
			{/* biome-ignore lint/a11y/noStaticElementInteractions: presentational sheet container; click handler only prevents event bubbling to the overlay */}
			<div
				className="playlist-sheet"
				style={sheetStyle}
				onClick={(event) => event.stopPropagation()}
			>
				<span aria-hidden="true" style={sheetGrabberStyle} />
				<SheetHeader title="排序方式" leading="cancel" onLeading={onClose} />
				<ul style={sheetBodyStyle} className="playlist-sheet__list">
					{SORT_OPTIONS.map((option) => {
						const isActive = option.value === selected;
						return (
							<li key={option.value}>
								<button
									type="button"
									className="playlist-sheet__item"
									onClick={() => onSelect(option.value)}
									aria-pressed={isActive}
								>
									{isActive ? (
										<ArtistIcon name="check" size={18} />
									) : (
										<span
											className="playlist-sheet__item-placeholder"
											aria-hidden="true"
										/>
									)}
									<span>{option.label}</span>
								</button>
							</li>
						);
					})}
				</ul>
			</div>
		</div>
	);
}

// Re-export for callers expecting the component under its previous name.
export type { DiscoveryPlaylist as Playlist };
